import React, { use } from "react";
import { clsx } from "clsx";
import { Text } from "./text";
import { ZxcvbnFactory, type ZxcvbnResult } from "@zxcvbn-ts/core";
import { useTranslation } from "react-i18next";

/**
 * Minimal required score ({@link ZxcvbnResult}) for a password to be considered safe enough
 */
export const MIN_PASSWORD_SCORE = 4;

let passwordCheckerPromise: Promise<ZxcvbnFactory> | undefined;

/**
 * Configures the {@link ZxcvbnFactory} password strength estimator.
 * Because this requires loading multiple multi-megabyte language packages this function is lazy and returns a `Promise`.
 *
 * Use it like this:
 * ```
 * const passwordStrengthChecker = use(loadPasswordStrengthChecker());
 * ```
 */
export function loadPasswordStrengthChecker(): Promise<ZxcvbnFactory> {
    if (passwordCheckerPromise === undefined) {
        passwordCheckerPromise = Promise.all([
            import("@zxcvbn-ts/language-common"),
            import("@zxcvbn-ts/language-en"),
            import("@zxcvbn-ts/language-de"),
        ]).then(([common, en, de]) => {
            return new ZxcvbnFactory({
                dictionary: {
                    ...common.dictionary,
                    ...en.dictionary,
                    ...de.dictionary,
                },
                graphs: common.adjacencyGraphs,
                translations: en.translations,
            });
        });
    }
    return passwordCheckerPromise;
}

/**
 * The properties for {@link PasswordStrength}
 */
export type PasswordStrengthProps = {
    /** The password to calculate the strength for */
    password: string;
};

/**
 * A display for the password strength
 */
export default function PasswordStrength(props: PasswordStrengthProps) {
    const [tg] = useTranslation();

    const { password } = props;

    const { crackTimes, score } = use(loadPasswordStrengthChecker()).check(password);

    const seconds = crackTimes.offlineSlowHashingXPerSecond.seconds;

    const century = 315360000;
    const year = 31536000;
    const month = 2678400;
    const week = 604800;
    const day = 86400;
    const hour = 3600;
    const minute = 60;

    const display =
        seconds / century > 1
            ? tg("label.centuries")
            : seconds / year > 1
              ? tg("label.years")
              : seconds / month > 1
                ? tg("label.months")
                : seconds / week > 1
                  ? tg("label.weeks")
                  : seconds / day > 1
                    ? tg("label.days")
                    : seconds / hour > 1
                      ? tg("label.hours")
                      : seconds / minute > 1
                        ? tg("label.minutes")
                        : tg("label.seconds");

    return (
        <div>
            <div className={"mt-3 mb-2 grid h-1 w-full grid-cols-4 gap-3"}>
                <div
                    className={clsx(
                        "rounded border border-zinc-400 dark:border-zinc-700",
                        score === 4 && "bg-green-500",
                        score === 3 && "bg-yellow-500",
                        score === 2 && "bg-orange-500",
                        (score === 1 || score === 0) && "bg-purple-500",
                    )}
                />
                <div
                    className={clsx(
                        "rounded border border-zinc-400 dark:border-zinc-700",
                        score === 4 && "bg-green-500",
                        score === 3 && "bg-yellow-500",
                        score === 2 && "bg-orange-500",
                    )}
                />
                <div
                    className={clsx(
                        "rounded border border-zinc-400 dark:border-zinc-700",
                        score === 4 && "bg-green-500",
                        score === 3 && "bg-yellow-500",
                    )}
                />
                <div
                    className={clsx(
                        "rounded border border-zinc-400 dark:border-zinc-700",
                        score === 4 && "bg-green-500",
                    )}
                />
            </div>
            <Text>{tg("label.estimated-crack-time", { time: display })}</Text>
        </div>
    );
}
